import { MapPin } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { DISPATCH_STATUSES, PRIORITY_STYLES, nextStatus, SECTION_LABEL, CARD } from './config';
import type { DispatchJob } from './hooks';

interface Props {
  jobs: DispatchJob[];
  onAdvance: (jobId: string, status: string) => void;
  onSelect: (job: DispatchJob) => void;
}

function elapsed(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diff < 1) return '<1m';
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
}

// TODO: Mapbox GL JS integration for map view — show job locations on a map

export function ActivePanel({ jobs, onAdvance, onSelect }: Props) {
  const dispatched = jobs.filter(j => j.status === 'assigned');
  const enRoute = jobs.filter(j => j.status === 'en_route');
  const onSite = jobs.filter(j => j.status === 'on_site');

  const Lane = ({ title, items, statusKey }: { title: string; items: DispatchJob[]; statusKey: string }) => {
    const st = DISPATCH_STATUSES[statusKey];
    const next = nextStatus(statusKey);
    const nextLabel = next ? DISPATCH_STATUSES[next]?.label : null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...SECTION_LABEL, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: st.color, display: 'inline-block' }} />
          {title} ({items.length})
        </div>
        {items.length === 0 ? null : (
          items.map(job => {
            const pr = PRIORITY_STYLES[job.priority ?? 'medium'] ?? PRIORITY_STYLES.medium;
            // Determine which timestamp to use for elapsed
            const timestamp = statusKey === 'on_site' ? (job as unknown as Record<string, unknown>).started_at as string
              : statusKey === 'en_route' ? (job as unknown as Record<string, unknown>).en_route_at as string
              : job.updated_at ?? job.created_at;
            return (
              <div key={job.id} onClick={() => onSelect(job)} style={CARD}
                onMouseEnter={e => (e.currentTarget.style.borderColor = palette.accent)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = palette.g150)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: pr.bg, color: pr.fg }}>{pr.label}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: palette.g800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</span>
                  <span style={{ fontSize: 10, color: st.color, fontWeight: 600 }}>{elapsed(timestamp)}</span>
                </div>
                <div style={{ fontSize: 11, color: palette.g500 }}>
                  {job.assigned_name ?? 'Unassigned'} {job.client_name ? `\u2022 ${job.client_name}` : ''}
                </div>
                {(job.address || job.location) && (
                  <div style={{ fontSize: 10, color: palette.g400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{job.address ?? job.location}</div>
                )}
                {next && nextLabel && (
                  <button onClick={e => { e.stopPropagation(); onAdvance(job.id, next); }} style={{
                    marginTop: 6, width: '100%', padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                    background: DISPATCH_STATUSES[next].bg, color: DISPATCH_STATUSES[next].color,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.15s',
                  }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >Mark {nextLabel}</button>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 10px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: palette.g800 }}>Active Board</div>
        <span style={{ fontSize: 11, color: palette.g400, fontWeight: 500 }}>{jobs.length} active</span>
      </div>

      <div>
        {jobs.length === 0 ? (
          <EmptyState icon={<MapPin size={22} />} title="No active dispatches" subtitle="Assign a job from the queue to get started." />
        ) : (
          <>
            <Lane title="Dispatched" items={dispatched} statusKey="assigned" />
            <Lane title="En Route" items={enRoute} statusKey="en_route" />
            <Lane title="On Site" items={onSite} statusKey="on_site" />
          </>
        )}
      </div>
    </div>
  );
}
